import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Table,
  Badge,
  Button,
  StatusDot,
  ProgressBar,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost } from "../lib/api";

interface LearningOverview {
  totalWorkstreams: number;
  activeExperiments: number;
  promotedMacros: number;
  fleetEpisodes: number;
  avgRewardScore: number;
  convergenceRate: number;
  lastSyncAt: string;
  pipelineStatus: string;
}

interface Workstream {
  id: string;
  name: string;
  tenantCount: number;
  totalEpisodes: number;
  avgReward: number;
  topModel: string;
  convergence: number;
}

interface Experiment {
  id: string;
  name: string;
  workstream: string;
  knob: string;
  variant: string;
  baseline: string;
  sampleSize: number;
  targetSampleSize: number;
  variantReward: number;
  baselineReward: number;
  pValue: number;
  status: "running" | "concluded";
  startedAt: string;
}

interface Macro {
  id: string;
  name: string;
  workstream: string;
  steps: string[];
  usageCount: number;
  avgReward: number;
  promotedAt: string;
  source: "experiment" | "operator";
}

interface PipelineStage {
  name: string;
  status: string;
  throughput: number;
  latencyMs: number;
}

interface PipelineData {
  lastIngestAt: string;
  episodesInQueue: number;
  processingRate: number;
  errorRate: number;
  stages: PipelineStage[];
}

const PIPELINE_STATUS_MAP: Record<string, "healthy" | "degraded" | "down"> = {
  healthy: "healthy",
  ok: "healthy",
  degraded: "degraded",
  warning: "degraded",
  error: "down",
  down: "down",
};

export default function LearningDashboard() {
  const {
    data: overview,
    isLoading: loadingOverview,
    error: errorOverview,
  } = useQuery<LearningOverview>({
    queryKey: ["learning", "overview"],
    queryFn: () => apiGet<LearningOverview>("/api/learning/overview"),
  });

  const { data: workstreams } = useQuery<Workstream[]>({
    queryKey: ["learning", "workstreams"],
    queryFn: async () => {
      const data = await apiGet<Workstream[] | { workstreams?: Workstream[] }>(
        "/api/learning/workstreams",
      );
      return Array.isArray(data) ? data : (data.workstreams ?? []);
    },
  });

  const { data: experiments } = useQuery<Experiment[]>({
    queryKey: ["learning", "experiments"],
    queryFn: async () => {
      const data = await apiGet<Experiment[] | { experiments?: Experiment[] }>(
        "/api/learning/experiments",
      );
      return Array.isArray(data) ? data : (data.experiments ?? []);
    },
  });

  const { data: macros } = useQuery<Macro[]>({
    queryKey: ["learning", "macros"],
    queryFn: async () => {
      const data = await apiGet<Macro[] | { macros?: Macro[] }>("/api/learning/macros");
      return Array.isArray(data) ? data : (data.macros ?? []);
    },
  });

  const { data: pipeline } = useQuery<PipelineData>({
    queryKey: ["learning", "pipeline"],
    queryFn: () => apiGet<PipelineData>("/api/learning/pipeline"),
  });

  usePolling(
    [
      ["learning", "overview"],
      ["learning", "workstreams"],
      ["learning", "experiments"],
      ["learning", "macros"],
      ["learning", "pipeline"],
    ],
    30_000,
  );

  const queryClient = useQueryClient();
  const stopExperiment = useMutation({
    mutationFn: (id: string) => apiPost(`/api/learning/experiments/${id}/stop`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["learning", "experiments"] }),
  });

  if (loadingOverview && !overview) {
    return (
      <div>
        <PageHeader title="Learning Dashboard" subtitle="Fleet-wide optimizer intelligence" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (errorOverview && !overview) {
    return (
      <div>
        <PageHeader title="Learning Dashboard" subtitle="Fleet-wide optimizer intelligence" />
        <QueryError
          error={errorOverview}
          queryKeys={[
            ["learning", "overview"],
            ["learning", "workstreams"],
            ["learning", "experiments"],
          ]}
        />
      </div>
    );
  }

  // Prepare chart data: top 15 workstreams by avgReward
  const chartData = [...(workstreams ?? [])]
    .sort((a, b) => b.avgReward - a.avgReward)
    .slice(0, 15)
    .map((ws) => ({ name: ws.name, avgReward: ws.avgReward }));

  const experimentColumns = [
    {
      key: "name",
      header: "Name",
      render: (row: Experiment) => (
        <span className="font-medium" style={{ color: "var(--c-text)" }}>
          {row.name}
        </span>
      ),
    },
    { key: "workstream", header: "Workstream" },
    { key: "knob", header: "Knob" },
    {
      key: "variant",
      header: "Variant vs Baseline",
      render: (row: Experiment) => (
        <span className="text-xs" style={{ color: "var(--c-text)" }}>
          {row.variantReward.toFixed(3)} vs {row.baselineReward.toFixed(3)}
        </span>
      ),
    },
    {
      key: "sampleSize",
      header: "Sample Size",
      render: (row: Experiment) => (
        <div className="space-y-1">
          <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
            {row.sampleSize} / {row.targetSampleSize}
          </span>
          <ProgressBar value={row.sampleSize} max={row.targetSampleSize} color="green" size="sm" />
        </div>
      ),
    },
    {
      key: "pValue",
      header: "p-Value",
      render: (row: Experiment) => (
        <span
          className="font-mono text-xs"
          style={{ color: row.pValue < 0.05 ? "var(--c-success)" : "var(--c-text-secondary)" }}
        >
          {row.pValue.toFixed(4)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row: Experiment) => (
        <Badge color={row.status === "running" ? "blue" : "green"}>{row.status}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row: Experiment) =>
        row.status === "running" ? (
          <Button
            size="sm"
            variant="danger"
            onClick={() => stopExperiment.mutate(row.id)}
            disabled={stopExperiment.isPending}
          >
            Stop
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader title="Learning Dashboard" subtitle="Fleet-wide optimizer intelligence" />

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Workstreams" value={String(overview?.totalWorkstreams ?? 0)} />
        <StatCard title="Fleet Episodes" value={(overview?.fleetEpisodes ?? 0).toLocaleString()} />
        <StatCard
          title="Avg Reward Score"
          value={(overview?.avgRewardScore ?? 0).toFixed(2)}
          color="var(--c-success)"
        />
        <StatCard
          title="Convergence Rate"
          value={((overview?.convergenceRate ?? 0) * 100).toFixed(0) + "%"}
        />
      </div>

      <div className="mb-8">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                Optimization Workflow
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--c-text-secondary)" }}>
                Engineering diagnostics live under Learning and are fed by Labs + Queue outcomes.
              </div>
            </div>
            <Link to="/learning/optimization" style={{ textDecoration: "none" }}>
              <Button size="sm" variant="secondary">
                Open Optimization
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Pipeline health */}
      {pipeline && (
        <div className="mb-8">
          <SectionHeader
            title="Pipeline Health"
            subtitle={`Last ingest: ${new Date(pipeline.lastIngestAt).toLocaleString()}`}
          />
          <Card>
            <div className="flex items-center gap-6 overflow-x-auto">
              {pipeline.stages.map((stage, i) => (
                <div key={stage.name} className="flex items-center gap-3">
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <StatusDot status={PIPELINE_STATUS_MAP[stage.status] ?? "unknown"} />
                      <span className="text-xs font-medium" style={{ color: "var(--c-text)" }}>
                        {stage.name}
                      </span>
                    </div>
                    <span className="text-[11px]" style={{ color: "var(--c-text-faint)" }}>
                      {stage.throughput}/s &middot; {stage.latencyMs}ms
                    </span>
                  </div>
                  {i < pipeline.stages.length - 1 && (
                    <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                      &rarr;
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Workstream reward chart */}
      {chartData.length > 0 && (
        <div className="mb-8">
          <SectionHeader
            title="Workstream Rewards"
            subtitle="Top 15 workstreams by average reward"
          />
          <Card>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 11, fill: "var(--c-text-secondary)" }}
                  interval={0}
                  height={80}
                />
                <YAxis domain={[0, 1]} tick={{ fontSize: 11, fill: "var(--c-text-secondary)" }} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: "var(--c-surface)",
                    border: "1px solid var(--c-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="avgReward" fill="#1A61ED" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Workstream list */}
      {(workstreams ?? []).length > 0 && (
        <div className="mb-8">
          <SectionHeader
            title="Workstreams"
            subtitle={`${(workstreams ?? []).length} workstreams detected`}
          />
          <Card padding={false}>
            <Table
              columns={[
                {
                  key: "name",
                  header: "Workstream",
                  render: (row: Workstream) => (
                    <Link
                      to={`/learning/workstreams/${row.id}`}
                      className="text-sm font-medium"
                      style={{ color: "var(--c-primary)" }}
                    >
                      {row.name}
                    </Link>
                  ),
                },
                { key: "tenantCount", header: "Tenants" },
                {
                  key: "totalEpisodes",
                  header: "Episodes",
                  render: (row: Workstream) => (
                    <span style={{ color: "var(--c-text)" }}>
                      {row.totalEpisodes.toLocaleString()}
                    </span>
                  ),
                },
                {
                  key: "avgReward",
                  header: "Avg Reward",
                  render: (row: Workstream) => (
                    <span className="font-mono text-xs" style={{ color: "var(--c-success)" }}>
                      {row.avgReward.toFixed(2)}
                    </span>
                  ),
                },
                { key: "topModel", header: "Top Model" },
                {
                  key: "convergence",
                  header: "Convergence",
                  render: (row: Workstream) => (
                    <ProgressBar value={row.convergence * 100} color="green" size="sm" />
                  ),
                },
              ]}
              data={(workstreams ?? []) as unknown as Record<string, unknown>[]}
              keyField="id"
              emptyMessage="No workstreams"
            />
          </Card>
        </div>
      )}

      {/* Active experiments */}
      <div className="mb-8">
        <SectionHeader
          title="Active Experiments"
          subtitle={`${(experiments ?? []).length} experiment${(experiments ?? []).length !== 1 ? "s" : ""}`}
        />
        <Card padding={false}>
          <Table
            columns={experimentColumns}
            data={(experiments ?? []) as unknown as Record<string, unknown>[]}
            keyField="id"
            emptyMessage="No experiments"
          />
        </Card>
      </div>

      {/* Promoted macros */}
      <SectionHeader
        title="Promoted Macros"
        subtitle={`${(macros ?? []).length} macro${(macros ?? []).length !== 1 ? "s" : ""} promoted`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(macros ?? []).map((macro) => (
          <Card key={macro.id}>
            <div
              style={{ borderLeft: "3px solid var(--c-success)", paddingLeft: 12, marginLeft: -20 }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                  {macro.name}
                </span>
                <Badge color="green">{macro.source}</Badge>
              </div>
              <p className="text-xs mb-2" style={{ color: "var(--c-text-secondary)" }}>
                {macro.workstream}
              </p>
              <div className="space-y-1 mb-3">
                {macro.steps.map((step, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-xs"
                    style={{ color: "var(--c-text-faint)" }}
                  >
                    <span
                      className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-medium"
                      style={{
                        border: "1px solid var(--c-border)",
                        color: "var(--c-text-secondary)",
                      }}
                    >
                      {i + 1}
                    </span>
                    {step}
                  </div>
                ))}
              </div>
              <div
                className="flex items-center gap-4 text-[11px]"
                style={{ color: "var(--c-text-faint)" }}
              >
                <span>{macro.usageCount} uses</span>
                <span>Avg reward: {macro.avgReward.toFixed(2)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
